import React, { useState } from "react";
import { Modal, Stack, Text, TextInput } from "@mantine/core";
import { Alert, Button, EmptyState, SectionCard } from "@xplatform/design-system";
import { IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { PermissionCatalogue } from "@models/access";
import { accessApi } from "@services/accessApi";
import { errorMessage } from "@hooks/monitoring/useApiResource";
import { AdminTable, col } from "@components/AdminTable";

type CatalogueModule = PermissionCatalogue["modules"][number];

interface ModulesCardProps {
  /** The catalogue's modules; `null` while loading or when they could not load. */
  modules: CatalogueModule[] | null;
  error: string | null;
  canManage: boolean;
  onCreated: () => void;
}

/** What the credential key of a module is: core lowercases the BeeHive name the same way. */
const moduleKey = (name: string) => name.trim().toLowerCase();
const VALID_KEY = /^[a-z0-9][a-z0-9_.-]{0,63}$/;

/**
 * The application's BeeHive modules (Translation, Mapping, ...): created here, empty, then
 * given their permissions (Access > Permissions) and granted provider credentials (Providers).
 * A module's key is its name lowercased: the segment of its SSM credentials path, the name
 * its service is deployed with.
 */
const ModulesCard: React.FC<ModulesCardProps> = ({ modules, error, canManage, onCreated }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const key = moduleKey(name);
  const invalid = name.trim() !== "" && !VALID_KEY.test(key);
  const exists = (modules ?? []).some((m) => moduleKey(m.name) === key);

  const open = () => {
    setName("");
    setDescription("");
    setSaveError(null);
    setAdding(true);
  };

  const submit = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await accessApi().createModule({ name: name.trim(), description: description.trim() || undefined });
      setAdding(false);
      onCreated();
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    col.text<CatalogueModule>("name", t("admin.access.modules.columns.name"), (m) => m.name),
    col.text<CatalogueModule>("key", t("admin.access.modules.columns.key"),
                              (m) => (VALID_KEY.test(moduleKey(m.name)) ? moduleKey(m.name) : t("admin.access.modules.noKey")),
                              { mono: true }),
    col.text<CatalogueModule>("permissions", t("admin.access.modules.columns.permissions"),
                              (m) => String(m.permissions.length)),
    col.text<CatalogueModule>("description", t("admin.access.modules.columns.description"), (m) => m.description),
  ];

  return (
    <SectionCard
      title={t("admin.access.modules.listTitle")}
      actions={canManage ? (
        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={open}>
          {t("admin.access.modules.add")}
        </Button>
      ) : undefined}
    >
      <Stack gap="xs">
        <Text size="sm" c="dimmed">{t("admin.access.modules.description")}</Text>
        {error ? (
          <Alert color="red">{error}</Alert>
        ) : modules && modules.length === 0 ? (
          <EmptyState title={t("admin.access.modules.empty")} description={t("admin.access.modules.emptyHint")} />
        ) : (
          <AdminTable columns={columns} data={modules ?? []} getRowKey={(m) => m.name} />
        )}
      </Stack>

      <Modal
        opened={adding}
        onClose={() => setAdding(false)}
        title={t("admin.access.modules.addTitle")}
        portalProps={{ target: "#admin-portal-root" }}
      >
        <Stack gap="md">
          <TextInput
            label={t("admin.access.modules.columns.name")}
            placeholder="Extraction"
            description={key && !invalid && !exists ? t("admin.access.modules.keyPreview", { key }) : undefined}
            error={invalid ? t("admin.access.modules.invalid") : exists ? t("admin.access.modules.exists") : undefined}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <TextInput
            label={t("admin.access.modules.columns.description")}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
          {saveError && <Alert color="red">{saveError}</Alert>}
          <Stack gap="xs" align="flex-end">
            <Button onClick={submit} loading={saving} disabled={!name.trim() || invalid || exists}>
              {t("admin.access.modules.add")}
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </SectionCard>
  );
};

export default ModulesCard;
